import cx from "classnames";
/* eslint-disable complexity */
import React from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { getLocale, useCurrentViewPort, ViewPort } from "@gamedev-web/gamedev-sdk-core";
import { BasicIconButton } from "@gaming/xds-react-js";
import { ChevronLeft } from "@gaming/xds-react-js-icons";

import {
  AccountManager,
  ApplicationStatusBox,
  DashboardHeader,
  MetaTags,
  OnboardingPageContainer,
  PitchHelpLink,
  PitchProgressBar,
  PitchSubmitButton,
  ProgramSelector,
} from "../../components";
import { getImage, ImageType } from "../../helpers/imageHelper";
import { FormPitchInformation } from "../../components/FormPitchInformation/FormPitchInformation";
import { LoadingSpinner } from "../../components/Spinner";
import { SplitPane } from "../../components/SplitPane";
import { PageType } from "../../constants/pageType";
import { comparePitchApplicationStepStatus } from "../../helpers/pitchStatusHelpers";
import {
  useProgramColorPicker,
  useProgramFillColorPicker,
} from "../../helpers/useProgramColorPicker";
import { useString, useStrings } from "../../localization/localizationHelpers";
import { BlockedReasons, CrmPitchConceptStatus } from "../../models/crmStatus";
import { Programs } from "../../models/programsConfig";
import { OnboardingProgram, PitchApplicationStep } from "../../models/status";
import { RouteNames } from "../../partnerConfig/routeNames";
import { OnboardingUrls } from "../../partnerConfig/routes";
import {
  getBlockedReason,
  getCurrentPitchConcept,
  getIsLoadingGameConcept,
  getPitchApplicationStep,
} from "../../redux/selectors";
import { PitchAccessDeniedPage } from "../AccessDeniedPage";
import { PitchBeforeYouStartBox } from "./PitchBeforeYouStartBox";
import * as styles from "./PitchHomePage.module.css";
import { PitchInformationReadOnly } from "./PitchInformationReadOnly";

const ExitButton = () => {
  const back = useString("back");
  const history = useHistory();
  const colorClass = useProgramColorPicker();
  const fillColorClass = useProgramFillColorPicker();

  return (
    <BasicIconButton
      as={"button"}
      onClick={() => history.push(OnboardingUrls[RouteNames.PitchWelcome])}
      icon={ChevronLeft}
      className={cx(styles.backButton, colorClass)}
      iconClassName={fillColorClass}
      role="link"
    >
      {back}
    </BasicIconButton>
  );
};

const PitchHomePage = () => {
  const [
    dashboardPitchIntroTitle,
    pitchConceptDashboardMainSubtitle,
    pitchConceptDashboardSubtitle,
    draft,
    submitted,
    reviewed,
    metaTitle,
    metaDescription,
    metaKeywords,
  ] = useStrings(
    "dashboardPitchIntroTitle",
    "pitchConceptDashboardMainSubtitle",
    "pitchConceptDashboardSubtitle",
    "draft",
    "submitted",
    "reviewed",
    "pitchHomeMetaTitle",
    "pitchHomeMetaDescription",
    "pitchHomeMetaKeywords"
  );

  const currentApplicationStep = useSelector(getPitchApplicationStep);
  const currentViewPort = useCurrentViewPort();
  const shouldShowRightPaneOnTop = currentViewPort < ViewPort.LG;
  const blockedReason = useSelector(getBlockedReason);
  const currentPitchConcept = useSelector(getCurrentPitchConcept);
  const isLoadingGameConcept = useSelector(getIsLoadingGameConcept);
  const locale = useSelector(getLocale);

  if (blockedReason !== undefined && blockedReason !== BlockedReasons.none) {
    return <PitchAccessDeniedPage />;
  }

  let statusMessage = draft;
  switch (currentPitchConcept?.crmPitchConceptStatus) {
    case CrmPitchConceptStatus.Submitted:
      statusMessage = submitted;
      break;
    case CrmPitchConceptStatus.Reviewed:
      statusMessage = reviewed;
      break;
  }

  const isBeforeNdaUnderReview =
    comparePitchApplicationStepStatus(currentApplicationStep, PitchApplicationStep.NdaUnderReview) <
    0;

  const isBeforeApplicationSubmitted =
    comparePitchApplicationStepStatus(
      currentApplicationStep,
      PitchApplicationStep.ApplicationSubmitted
    ) < 0;
  const rightPane = (
    <>
      <div className={styles.linkPaneContainer}>
        <PitchHelpLink />
      </div>
      <ApplicationStatusBox program={Programs.Pitch} />
      <PitchProgressBar />
      <PitchSubmitButton />
    </>
  );

  return (
    <>
      <MetaTags
        title={metaTitle}
        description={metaDescription}
        keywords={metaKeywords}
        canonicalUrl={`https://developer.microsoft.com/${locale}/games/publish/pitch`}
        imageUrl={getImage("idxboxlogo", ImageType.Png)}
      />

      <OnboardingPageContainer pageType={PageType.PitchApplication}>
        <SplitPane rightPane={!shouldShowRightPaneOnTop ? rightPane : undefined}>
          <DashboardHeader
            title={dashboardPitchIntroTitle}
            mainSubtitle={pitchConceptDashboardMainSubtitle}
            subtitle={pitchConceptDashboardSubtitle}
            isFormatRequired={true}
          />
          <ExitButton />
          {shouldShowRightPaneOnTop && rightPane}
          <AccountManager
            isNdaUnderReviewStepOrAfter={!isBeforeNdaUnderReview}
            program={Programs.Pitch}
            hideCompanyInformationRow
          />
          <ProgramSelector selectedProgram={OnboardingProgram.PitchProgram} />
          <PitchBeforeYouStartBox />
          <>
            {isLoadingGameConcept ? (
              <div className={styles.spinnerWrapper}>
                <LoadingSpinner />
              </div>
            ) : (
              <>
                {isBeforeApplicationSubmitted ? (
                  <FormPitchInformation statusMessage={statusMessage} />
                ) : (
                  <PitchInformationReadOnly />
                )}
              </>
            )}
          </>
        </SplitPane>
      </OnboardingPageContainer>
    </>
  );
};

export default PitchHomePage;
